<template>
  <WdsStack direction-column class="tw-rounded">
    <HirePageSectionHeader>
      <template #title>
        <WdsStack gap-sm align-center class="tw-flex-wrap lg:tw-flex-nowrap">
          <WdsHeading type="title3" class="tw-pl-2">
            {{ $t('Additional information') }}
          </WdsHeading>
        </WdsStack>
      </template>
      <template #actions>
        <WdsButtonGhost
          v-if="hire"
          secondary
          class="tw-text-primary tw-font-medium"
          data-test="hire-internal-fields-manage"
          :href="jobCustomFieldsUrl"
        >
          <WdsStack justify-center align-center gap-xs>
            {{ $t('Manage') }}
            <WdsIcon name="chevron-right" :size="16" primary />
          </WdsStack>
        </WdsButtonGhost>
      </template>
    </HirePageSectionHeader>
    <Transition name="fade">
      <WdsStack v-if="loading" align-start direction-column gap-sm class="tw-w-full">
        <LoadingList :items="4" />
      </WdsStack>

      <div v-else-if="hire" class="tw-px-2">
        <div class="tw-grid tw-grid-cols-3 tw-gap-4 tw-pt-4 tw-pb-2">
          <WdsText v-if="isCompany" class="tw-font-medium">
            <span class="tw-text-gray-800">{{ $t('Internal budget') }}</span>
          </WdsText>
          <WdsText
            v-if="isCompany"
            :secondary="!internalBudget"
            :text="internalBudget ? internalBudget + ' ' + hire.job.currency : $t('Not specified')"
            class="tw-col-span-2"
          />

          <WdsText v-if="projectTrackingEnabled" class="tw-font-medium">
            <span class="tw-text-gray-800">{{ $t('Project') }}</span>
          </WdsText>
          <WdsText
            v-if="projectTrackingEnabled"
            :secondary="!hire.job.project"
            :text="hire.job.project?.name ?? $t('Not specified')"
            class="tw-col-span-2"
          />
        </div>

        <HireCustomFieldsGroup
          v-if="hasHireFields"
          data-test="hire-custom-fields"
          :title="$t('Hire custom fields')"
          :subtitle="$t('Specific to this hire, so changes only apply here')"
          :sections="hireFieldSections"
          :editable="companyHireFields.length > 0"
          edit-data-test="hire-custom-fields-edit"
          @edit="openEditModal"
        />

        <HireCustomFieldsGroup
          v-if="hasJobFields"
          data-test="job-custom-fields"
          :title="hasHireFields ? $t('Job custom fields') : $t('Custom fields')"
          :subtitle="$t('Inherited from the job, so changes apply to every hire on it')"
          :tooltip="hasStaffingAgencySharedFields ? $t('Some fields below are shared outside your team.') : undefined"
          :sections="[{ key: 'job', fields: jobFieldList, values: jobFields }]"
          :editable="!!hire"
          :edit-href="jobCustomFieldsUrl"
        />
      </div>
    </Transition>
  </WdsStack>

  <HireCustomFieldsModal
    :open="editing"
    :title="$t('Edit hire fields')"
    :fields="companyHireFields"
    :field-values="hireFields.localFieldValues"
    :field-value-fragments="hireFields.initialFieldValueFragments"
    :errors="hireFields.fieldErrors"
    :loading="hireFields.fieldsLoading"
    :saving="hireFields.saving"
    :failed="failed"
    data-test="hire-custom-fields-modal"
    @update:field-values="onEditHireFields"
    @cancel="closeEditModal"
    @save="onSaveHireFields"
  />
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsIcon,
  WdsStack,
  WdsButtonGhost,
  WdsHeading,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, reactive, ref } from 'vue'
import { graphql, useFragment } from '@/gql'
import { HireFragment, HireJobBudgetFragment } from '@/components/Hire/graphql/fragments'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import HireCustomFieldsGroup from '@/components/Hire/HirePageParts/HireCustomFieldsGroup.vue'
import HirePageSectionHeader from '@/components/Hire/HirePageParts/HirePageSectionHeader.vue'
import HireCustomFieldsModal from '@/components/Hire/HirePageParts/HireCustomFieldsModal.vue'
import useProfileModalCustomFields from '@/components/Fieldable/composables/useProfileModalCustomFields'
import { mapCustomFieldValueToCustomFieldValueInput } from '@/components/Fieldable/utils'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { CUSTOM_FIELD_VALUES_VIEW_FRAGMENT } from '@/components/Fieldable/graphql/fragments'
import { useAccountSettings } from '@/composables/useAccountSettings'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { FieldableFormCustomFieldsQuery } from '@/components/Fieldable/graphql/queries'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useHireCustomFieldSections } from '@/components/Hire/composables/useHireCustomFieldSections'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'

const props = defineProps<{ hireId: string }>()

const { result, loading, refetch } = useQuery(
  graphql(`
    query HireInternalFields($id: ID!) {
      hire(id: $id) {
        ...HireType
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const { isCompany } = useAccountCapabilities()
const { hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)

const { result: hireJobBudgetResult } = useQuery(
  graphql(`
    query HireJobBudget($id: ID!) {
      hire(id: $id) {
        ...HireJobBudget
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
    enabled: isCompany,
  },
)

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const projectTrackingEnabled = useAccountSettings('project_tracking')

const internalBudget = computed(
  () =>
    useFragment(HireJobBudgetFragment, hireJobBudgetResult.value?.hire)?.job?.internalBudget ?? '',
)
const jobFields = computed(() => hire.value?.job.customFieldValues ?? [])

// Owners, internal budget and job custom fields are all edited on the job's custom fields page.
const jobCustomFieldsUrl = computed(() =>
  hire.value
    ? laravelNamedRoute('yjob.custom-fields', {
        yjob: hire.value.job?.id,
        // eslint-disable-next-line vue/camelcase
        from_hire: hire.value.id,
        // eslint-disable-next-line vue/camelcase
        redirect_uri: laravelNamedRoute('hires.hire.view', { hire: hire.value.id, tab: 'overview' }),
      })
    : undefined,
)

const { result: jobFieldsResult } = useQuery(
  FieldableFormCustomFieldsQuery,
  () => ({
    viewerAccount: currentAccount.globalId,
    accounts: [currentAccount.globalId],
    appliesTo: [TypeSupportingCustomFieldValues.Job],
    first: PER_PAGE_MAXIMUM,
  }),
  {
    clientId: 'publicApi',
    enabled: isCompany,
  },
)
const jobFieldList = computed(() => jobFieldsResult.value?.customFields?.data ?? [])
const hasJobFields = computed(() => jobFields.value.length > 0)

const hasStaffingAgencySharedFields = computed(() =>
  jobFields.value.some(
    (customFieldValue) =>
      useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, customFieldValue).customField
        .sharedWithStaffingAgencies,
  ),
)


const hireCustomFieldValues = computed(() => hire.value?.customFieldValues ?? [])

const hireFields = reactive(
  useProfileModalCustomFields(
    currentAccount.globalId,
    props.hireId,
    TypeSupportingCustomFieldValues.Hire,
    isCompany,
    hireCustomFieldValues,
  ),
)

// Fields the worker or agency answers are listed apart, so it's clear why the edit modal leaves them out.
const {
  companyFields: companyHireFields,
  hasFields: hasHireFields,
  sections: hireFieldSections,
} = useHireCustomFieldSections({
  fields: () => hireFields.fields,
  hire,
  recruiterManagesWorker: hasRecruiterManagingWorkers,
})

const editing = ref(false)
const failed = ref(false)

function openEditModal() {
  failed.value = false
  editing.value = true
}

// Cancelling drops the edits; the composable keeps whatever was typed until told otherwise.
function closeEditModal() {
  hireFields.localFieldValues = mapCustomFieldValueToCustomFieldValueInput(
    hireCustomFieldValues.value,
  )
  editing.value = false
}

function onEditHireFields(values: CustomFieldTypeValueInput[]) {
  hireFields.localFieldValues = values
  failed.value = false
}

async function onSaveHireFields() {
  const ok = await hireFields.handleSave()
  failed.value = !ok

  if (!ok) {
    return
  }

  editing.value = false
  await refetch()
}
</script>

<style lang="scss" scoped>
.gradient-bar {
  background: linear-gradient(90deg, #fd8a33 0%, #fbc5c5 40%, #ffc89e 100%);
  border-radius: 3px 3px 0 0;
}

.section {
  border-radius: 0 0 3px 3px;
}
</style>
